import * as React from "react";
import { Button } from "@mui/material";
import { ButtonProps } from "@mui/material/Button";

import { useIsMonthEndLockActive } from "hooks/useMonthEndStatus";
import { MAINTENANCE_TOOLTIP } from "modules/maintenance/messages";

export interface MaintenanceLockButtonProps extends ButtonProps {
    component?: string | ((props: any) => JSX.Element);
}

/**
 * A Button that disables itself while the Vital Objects month-end job is running.
 *
 * Used for the controls that *open* an edit, so a member is not invited to start something they
 * cannot finish. MaintenanceLockSubmitButton is the equivalent for the submit inside the form.
 *
 * Neither is the protection - both read a status that only refreshes on mount and window focus, so
 * either can be looking at a stale value. The save thunks re-check at submit time, which cannot go
 * stale and leaves the member's typed data intact. See docs/Month End Lockout.md.
 *
 * Modelled on NoEmulationButton, and independent of it: emulation is enforced at the form submit.
 */
function MaintenanceLockButton(props: MaintenanceLockButtonProps) {
    const isMonthEndLockActive = useIsMonthEndLockActive();

    const { title, children, disabled, ...rest } = props;

    // The wrapping span carries the tooltip because MUI disabled buttons swallow pointer events.
    return (
        <span title={isMonthEndLockActive ? MAINTENANCE_TOOLTIP : title}>
            <Button
                {...rest}
                disabled={disabled || isMonthEndLockActive}
                title={isMonthEndLockActive ? MAINTENANCE_TOOLTIP : title}>
                {children}
            </Button>
        </span>
    );
}

export default MaintenanceLockButton;
